import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { login as loginService } from "../../services";
import { login } from "../../store/slices/UserSlice";
import { toggleTheme } from "../../store/slices/themeSlice";
import { encryptToken } from "../../utils/globals";
import darkLogo from "../../assets/img/darktheme-logo.png";
import lightLogo from "../../assets/img/lighttheme-logo.png";

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const themeMode = useSelector((state) => state.theme.mode);
  const isDark = themeMode === "dark";
  const logoSrc = isDark ? darkLogo : lightLogo;

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    passwordType: "password",
    isLoading: false,
    errMsg: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordType = () => {
    setFormData((prev) => ({
      ...prev,
      passwordType: prev.passwordType === "password" ? "text" : "password",
    }));
  };

  const validateForm = () => {
    if (!formData.username || !formData.password) {
      setFormData((prev) => ({
        ...prev,
        errMsg: "Both email and password are required.",
      }));
      return false;
    }
    return true;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setFormData((prev) => ({ ...prev, isLoading: true, errMsg: "" }));

    try {
      const response = await loginService({
        EmailAddress: formData.username,
        Password: formData.password,
      });

      if (response?.status === 200) {
        const { AccessToken, RefreshToken, ...memberInfo } = response.data.Result;
        const encryptedToken = encryptToken(AccessToken);
        const encryptedRefreshToken = encryptToken(RefreshToken);
        localStorage.setItem("AccessToken", encryptedToken);
        localStorage.setItem("RefreshToken", encryptedRefreshToken);
        dispatch(login(memberInfo));
        navigate("/");
      } else {
        setFormData((prev) => ({
          ...prev,
          errMsg: response?.data?.Message || "Login failed. Please try again.",
        }));
      }
    } catch (error) {
      console.error("Login error:", error);
      setFormData((prev) => ({
        ...prev,
        errMsg:
          error.response?.data?.Message ||
          "An unexpected error occurred. Please try again later.",
      }));
    } finally {
      setFormData((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gray-100 dark:bg-[#06060a]">
      {/* Left: branding (desktop) */}
      <div
        className="relative hidden lg:flex lg:w-[48%] xl:w-1/2 min-h-screen items-center justify-center overflow-hidden
        bg-gradient-to-br from-slate-200 via-slate-100 to-white
        dark:from-[#030712] dark:via-[#0a0f1c] dark:to-[#020617]"
      >
        {/* Subtle glow + star-like dots (dark only) */}
        <div
          className="pointer-events-none absolute inset-0 opacity-0 dark:opacity-100 transition-opacity duration-500"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 80% 50% at 20% 30%, rgba(59, 130, 246, 0.18), transparent 55%),
              radial-gradient(ellipse 60% 40% at 85% 70%, rgba(99, 102, 241, 0.14), transparent 50%),
              radial-gradient(1px 1px at 10% 20%, rgba(255,255,255,0.35) 50%, transparent 51%),
              radial-gradient(1px 1px at 30% 80%, rgba(255,255,255,0.25) 50%, transparent 51%),
              radial-gradient(1px 1px at 70% 15%, rgba(255,255,255,0.2) 50%, transparent 51%),
              radial-gradient(1px 1px at 90% 40%, rgba(255,255,255,0.28) 50%, transparent 51%)
            `,
          }}
        />
        <div className="relative z-10 flex flex-col items-center justify-center px-10 py-12 max-w-lg">
          <img
            src={logoSrc}
            alt="Gentrick Staffing — Attendance Portal"
            className="w-full max-w-[min(100%,300px)] h-auto object-contain drop-shadow-xl dark:drop-shadow-[0_0_40px_rgba(59,130,246,0.15)]"
          />
        </div>
      </div>

      {/* Divider */}
      <div
        className="hidden lg:block w-px shrink-0 bg-gradient-to-b from-transparent via-slate-300/60 to-transparent dark:via-white/10"
        aria-hidden="true"
      />

      {/* Right: form */}
      <div className="flex-1 flex flex-col min-h-screen lg:min-h-0">
        <div className="flex justify-end px-5 pt-5 lg:px-10 lg:pt-8">
          <button
            type="button"
            onClick={() => dispatch(toggleTheme())}
            className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 bg-white/80 dark:bg-white/5 border border-slate-200/80 dark:border-white/10 hover:bg-white dark:hover:bg-white/10 transition-colors shadow-sm"
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDark ? (
              <>
                <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
                </svg>
                Light
              </>
            ) : (
              <>
                <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    fillRule="evenodd"
                    d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z"
                    clipRule="evenodd"
                  />
                </svg>
                Dark
              </>
            )}
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center px-5 pb-12 lg:px-12 lg:pb-16">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="lg:hidden flex justify-center mb-8">
              <img
                src={logoSrc}
                alt="Gentrick Staffing — Attendance Portal"
                className="w-full max-w-[220px] h-auto object-contain"
              />
            </div>

            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-white tracking-tight">
              Welcome to Gentrick Staffing
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Attendance Portal — sign in with your work email
            </p>

            <form
              onSubmit={handleSubmit}
              className="mt-8 space-y-4 rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white/90 dark:bg-white/[0.04] backdrop-blur-xl p-6 sm:p-8 shadow-xl shadow-slate-200/40 dark:shadow-none"
            >
              <div>
                <label
                  htmlFor="UserEmail"
                  className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2"
                >
                  Email
                </label>
                <input
                  className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-black/40 py-3 px-4 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 transition-shadow"
                  id="UserEmail"
                  name="username"
                  type="text"
                  autoComplete="username"
                  placeholder="Email address"
                  value={formData.username}
                  onChange={handleInputChange}
                  aria-label="Email address"
                />
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-black/40 py-3 pl-4 pr-12 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 transition-shadow"
                    id="password"
                    name="password"
                    type={formData.passwordType}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleInputChange}
                    aria-label="Password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors"
                    onClick={togglePasswordType}
                    aria-label={
                      formData.passwordType === "password"
                        ? "Show password"
                        : "Hide password"
                    }
                  >
                    {formData.passwordType === "password" ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {formData.errMsg && (
                <div
                  className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300"
                  role="alert"
                >
                  {formData.errMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={formData.isLoading}
                aria-busy={formData.isLoading}
                aria-live="polite"
                className="group w-full flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-500 dark:bg-blue-600 dark:hover:bg-blue-500 text-white font-semibold py-3.5 px-6 shadow-lg shadow-blue-600/25 dark:shadow-blue-900/40 transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
              >
                {formData.isLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <>
                    Login
                    <span
                      className="inline-block transition-transform group-hover:translate-x-0.5"
                      aria-hidden="true"
                    >
                      →
                    </span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
